import React, { useState, useEffect } from "react";
import { Table, Tag, Pagination, Dropdown, Menu, Modal, message } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useLocationsPaginated, Location } from "../../hooks/useLocation";

interface LocationTableProps {
  filters: Record<string, any>;
  onModifyLocation: (location: Location) => void;
  onDeleteLocation: (locationId: number) => void;
  canModifyLocations: boolean; // Permission for modify
  canDeleteLocations: boolean; // Permission for delete
}

const LocationTable: React.FC<LocationTableProps> = ({
  filters,
  onModifyLocation,
  onDeleteLocation,
  canModifyLocations,
  canDeleteLocations,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const { data: locationsData, isLoading: locationsLoading } =
    useLocationsPaginated(currentPage, filters);

  // Handle delete confirmation
  const handleDelete = (locationId: number) => {
    if (!canDeleteLocations) {
      message.error("You don't have permission to delete locations");
      return;
    }

    Modal.confirm({
      title: "Are you sure you want to delete this location?",
      content:
        "This action cannot be undone and may affect users assigned to this location.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => onDeleteLocation(locationId),
    });
  };

  // Handle modify location
  const handleModify = (location: Location) => {
    if (!canModifyLocations) {
      message.error("You don't have permission to modify locations");
      return;
    }

    onModifyLocation(location);
  };

  // Dynamically build menu items based on permissions
  const getMenuItems = (record: Location) => {
    const items = [];

    if (canModifyLocations) {
      items.push(
        <Menu.Item
          key="modify"
          icon={<EditOutlined />}
          onClick={() => handleModify(record)}
        >
          Modify
        </Menu.Item>
      );
    }

    if (canDeleteLocations) {
      items.push(
        <Menu.Item
          key="delete"
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
          danger
        >
          Delete
        </Menu.Item>
      );
    }

    return items;
  };

  // Format currency for display
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const columns: ColumnsType<Location> = [
    {
      title: "Location Name",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      render: (name, record) => (
        <div className="flex items-center space-x-2">
          <EnvironmentOutlined className="text-primary" />
          <span className="font-medium">{name}</span>
        </div>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
    },
    {
      title: "Location Code",
      dataIndex: "code",
      key: "code",
      render: (code) => (
        <Tag color="blue" className="font-mono">
          {code}
        </Tag>
      ),
    },
    {
      title: "Minimum Order Amount",
      dataIndex: "minimum_order_amount",
      key: "minimum_order_amount",
      render: (amount) => (
        <span className="font-medium text-green-600">
          {formatCurrency(amount)}
        </span>
      ),
      sorter: (a, b) => a.minimum_order_amount - b.minimum_order_amount,
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) =>
        new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
    },
  ];

  // Only add Action column if user has either modify or delete permission
  if (canModifyLocations || canDeleteLocations) {
    columns.push({
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => {
        const menuItems = getMenuItems(record);

        // If no permissions, don't show dropdown
        if (menuItems.length === 0) {
          return null;
        }

        return (
          <Dropdown overlay={<Menu>{menuItems}</Menu>} trigger={["click"]}>
            <button className="text-blue-500 hover:text-blue-700">
              <EllipsisOutlined />
            </button>
          </Dropdown>
        );
      },
    });
  }

  return (
    <div>
      <Table
        dataSource={locationsData?.data ?? []}
        columns={columns}
        pagination={false}
        loading={locationsLoading}
        bordered
        scroll={{ x: "max-content" }}
        className="custom-table"
        rowKey="id"
      />

      {/* Custom Pagination */}
      <div className="flex justify-end mt-4">
        <Pagination
          current={locationsData?.meta.current_page ?? 1}
          total={locationsData?.meta.total ?? 0}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
};

export default LocationTable;
